/**
 * ai/AiScreenContext.jsx
 *
 * Lets deeply nested screens (sidebar sub-views, SuvNav tabs) report their
 * active sub-screen to the AI chat widget without prop drilling through Layout.
 *
 * Multiple reporters stack: the most recently registered wins. On unmount the
 * previous reporter (e.g. parent sidebar) is restored automatically.
 *
 * Usage in a module with sidebar (e.g. banks.jsx):
 *
 *   import { useReportAiSubScreen } from '../../ai/AiScreenContext'
 *
 *   const Banks = () => {
 *     const [visibleSubModule, setVisibleSubModule] = useState('banks_movements')
 *     useReportAiSubScreen(visibleSubModule)
 *     ...
 *   }
 *
 * Nested SuvNav (reports only when a tab is selected; parent keeps context otherwise):
 *
 *   useReportAiSubScreen(visibleSubModule ? TAB_MAP[visibleSubModule] : null)
 *
 * AiChat reads subScreen and builds screenId: "fin-ban:banks_reconciliation"
 */

import React, { createContext, useContext, useState, useEffect, useId, useCallback, useMemo } from 'react'

const AiScreenContext = createContext(null)

export function AiScreenProvider({ children }) {
  const [reporters, setReporters] = useState([])

  const register = useCallback((id, subScreenId) => {
    setReporters((prev) => {
      const filtered = prev.filter((r) => r.id !== id)
      return [...filtered, { id, subScreenId }]
    })
  }, [])

  const unregister = useCallback((id) => {
    setReporters((prev) => prev.filter((r) => r.id !== id))
  }, [])

  const subScreen = reporters.length > 0
    ? reporters[reporters.length - 1].subScreenId
    : null

  const value = useMemo(
    () => ({ subScreen, register, unregister }),
    [subScreen, register, unregister]
  )

  return (
    <AiScreenContext.Provider value={value}>
      {children}
    </AiScreenContext.Provider>
  )
}

export function useAiSubScreen() {
  const ctx = useContext(AiScreenContext)
  if (!ctx) {
    return { subScreen: null, register: () => {}, unregister: () => {} }
  }
  return ctx
}

/**
 * Call from any screen that has an internal sidebar / sub-view / SuvNav tab.
 * Pass null to defer to a parent reporter (nested SuvNav before first tab click).
 *
 * @param {string|null} subScreenId  e.g. "banks_reconciliation", "accounts_current"
 */
export function useReportAiSubScreen(subScreenId) {
  const { register, unregister } = useAiSubScreen()
  const id = useId()

  useEffect(() => {
    if (!subScreenId) {
      return undefined
    }
    register(id, subScreenId)
    return () => unregister(id)
  }, [subScreenId, id, register, unregister])
}

/**
 * Map SuvNav tab ids (sub_nav_0, …) to stable sub-screen ids.
 *
 * @param {string|null|undefined} tabId
 * @param {Record<string, string>} tabMap
 * @param {string|null} [fallback]  used when tabId is set but not in map
 */
export function useReportAiSubScreenTab(tabId, tabMap, fallback = null) {
  const subId = tabId ? (tabMap[tabId] ?? fallback) : null
  useReportAiSubScreen(subId)
}
